import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Upload,
  Download,
  FileText,
  Search,
  ShieldCheck,
  Trash2,
} from "lucide-react";

const API_BASE =
  "https://kjptq1m402.execute-api.us-east-1.amazonaws.com/prod";

function App() {
  const [files, setFiles] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  /* ================= FETCH FILES ================= */

  const fetchFiles = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/files`);

      const data = await res.json();

      setFiles(data.files || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  /* ================= UPLOAD ================= */    
const handleUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  try {
    // Step 1: Get presigned URL
    const res = await fetch(`${API_BASE}/upload`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
      }),
    });

    const data = await res.json();

    console.log("UPLOAD URL:", data.uploadUrl);

    if (!data.uploadUrl) {
      alert("Upload URL not received");
      return;
    }

    // 🚨 IMPORTANT: Upload DIRECTLY to S3 (NOT CloudFront)
    const uploadRes = await fetch(data.uploadUrl, {
      method: "PUT",
      body: file,
    });

    if (!uploadRes.ok) {
      const error = await uploadRes.text();
      console.error("Upload failed:", error);
      alert("Upload failed");
      return;
    }

    alert("Upload successful ✅");

    fetchFiles();

  } catch (err) {
    console.error("Upload error:", err);
  }
};

  /* ================= DOWNLOAD ================= */

  const handleDownload = async (fileId) => {
    try {
      const res = await fetch(
        `${API_BASE}/download?fileId=${encodeURIComponent(fileId)}`
      );

      const data = await res.json();

      if (!data.downloadUrl) {
        alert("Download URL not received");
        return;
      }

      window.open(data.downloadUrl, "_blank");
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  /* ================= DELETE ================= */

  const handleDelete = async (fileId) => {
    try {
      const res = await fetch(`${API_BASE}/files`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ fileId }),
      });

      if (!res.ok) {
        alert("Delete failed");
        return;
      }

      fetchFiles();
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const filteredFiles = files.filter((file) =>
    file.fileName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h2 style={styles.title}>
          <ShieldCheck size={28} /> Secure File Storage
        </h2>
        <p style={styles.subtitle}>AWS Powered • Encrypted</p>
      </div>

      <div style={styles.actionBar}>
        <label style={styles.uploadBtn}>
          <Upload size={16} /> Upload File
          <input
            type="file"
            style={{ display: "none" }}
            onChange={handleUpload}
          />
        </label>

        <div style={styles.searchBox}>
          <Search size={16} />
          <input
            placeholder="Search files..."
            style={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading && <p>Loading files...</p>}

      {filteredFiles.map((file, index) => (
        <motion.div
          key={file.fileId}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.08 }}
          style={styles.card}
        >
          <div style={styles.fileLeft}>
            <div style={styles.fileIcon}>
              <FileText size={28} />
            </div>
            <div>
              <div style={styles.fileName}>{file.fileName}</div>
              <div style={styles.fileMeta}>
                Uploaded {new Date(file.uploadedAt).toLocaleString()}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <button
              style={styles.downloadBtn}
              onClick={() => handleDownload(file.fileId)}
            >
              <Download size={16} /> Download
            </button>

            <button
              style={styles.deleteBtn}
              onClick={() => handleDelete(file.fileId)}
            >
              <Trash2 size={16} /> Delete
            </button>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

export default App;
const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #eef2ff, #f8fafc)",
    padding: "24px",
    fontFamily: "Segoe UI, Arial",
  },

  header: {
    marginBottom: "24px",
  },

  title: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    margin: 0,
  },

  subtitle: {
    marginTop: "4px",
    fontSize: "13px",
    color: "#64748b",
  },

  actionBar: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "10px",
  },

  uploadBtn: {
    background: "#2563eb",
    color: "#fff",
    padding: "12px 18px",
    borderRadius: "12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  searchBox: {
    background: "#fff",
    borderRadius: "12px",
    padding: "10px 14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  searchInput: {
    border: "none",
    outline: "none",
  },

  card: {
    background: "#fff",
    padding: "16px",
    marginBottom: "14px",
    borderRadius: "14px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  fileLeft: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },

  fileIcon: {
    background: "#dbeafe",
    padding: "10px",
    borderRadius: "10px",
  },

  fileName: {
    fontWeight: "600",
  },

  fileMeta: {
    fontSize: "12px",
    color: "#64748b",
  },

  downloadBtn: {
    background: "#f1f5f9",
    padding: "8px 12px",
    borderRadius: "8px",
    cursor: "pointer",
    border: "none",
  },

  deleteBtn: {
    background: "#fee2e2",
    padding: "8px 12px",
    borderRadius: "8px",
    cursor: "pointer",
    border: "none",
  },
  navbar: {
  background: "#ffffff",
  padding: "14px 22px",
  borderRadius: "12px",
  marginBottom: "24px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  boxShadow: "0 4px 10px rgba(0,0,0,0.05)"
},

logo: {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  fontWeight: "600",
  fontSize: "18px"
},

navRight: {
  fontSize: "13px",
  color: "#64748b"
},
};